import React, { useState } from 'react';
import {
  Typography, Box, Chip, TextField, InputAdornment, IconButton, Tooltip, Collapse,
} from '@mui/material';
import {
  Search, ContentCopy, CheckCircle, ExpandMore, ExpandLess,
} from '@mui/icons-material';
import { SUPPORTED_METRICS, METRIC_CATEGORIES, MetricDefinition } from '../config/sensorMetrics';

interface SensorMetricsReferenceProps {
  protocol?: 'http' | 'mqtt' | 'both';
}

const SensorMetricsReference: React.FC<SensorMetricsReferenceProps> = ({ protocol = 'both' }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  const handleCopyField = (fieldName: string) => {
    navigator.clipboard.writeText(fieldName);
    setCopiedField(fieldName);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const toggleCategory = (cat: string) => {
    setExpandedCategories(prev => {
      const next = new Set(prev);
      if (next.has(cat)) next.delete(cat); else next.add(cat);
      return next;
    });
  };

  const filterMetrics = (metrics: MetricDefinition[]) => {
    if (!searchTerm) return metrics;
    const term = searchTerm.toLowerCase();
    return metrics.filter(m =>
      m.userFriendly.toLowerCase().includes(term) ||
      m.internal.toLowerCase().includes(term) ||
      m.description.toLowerCase().includes(term) ||
      m.unit.toLowerCase().includes(term)
    );
  };

  const getProtocolText = () => {
    if (protocol === 'http') return 'HTTP API';
    if (protocol === 'mqtt') return 'MQTT';
    return 'HTTP & MQTT';
  };

  const filteredMetrics = filterMetrics(SUPPORTED_METRICS);

  return (
    <Box>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Supported Sensor Metrics
        </Typography>
        <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mb: 2 }}>
          Send any combination of these measurements via {getProtocolText()}.
          Use the <strong>Field Name</strong> in your payload. Click to copy.
        </Typography>

        <TextField
          fullWidth size="small"
          placeholder="Search metrics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: <InputAdornment position="start"><Search sx={{ color: 'rgba(255,255,255,0.3)' }} /></InputAdornment>,
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      {METRIC_CATEGORIES.map(category => {
        const categoryMetrics = filteredMetrics.filter(m => m.category === category);
        if (categoryMetrics.length === 0) return null;
        const isExpanded = expandedCategories.has(category);

        return (
          <Box key={category} sx={{ mb: 1 }}>
            <Box
              onClick={() => toggleCategory(category)}
              sx={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                p: 1.5, borderRadius: 1, cursor: 'pointer',
                background: 'rgba(0,212,255,0.06)', border: '1px solid rgba(0,212,255,0.12)',
                '&:hover': { background: 'rgba(0,212,255,0.1)' },
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#00d4ff' }}>
                  {category}
                </Typography>
                <Chip label={`${categoryMetrics.length}`} size="small"
                  sx={{ height: 20, fontSize: '0.7rem', background: 'rgba(0,212,255,0.15)', color: '#00d4ff' }} />
              </Box>
              {isExpanded ? <ExpandLess sx={{ color: 'rgba(255,255,255,0.4)' }} /> : <ExpandMore sx={{ color: 'rgba(255,255,255,0.4)' }} />}
            </Box>

            <Collapse in={isExpanded}>
              <Box sx={{ mt: 0.5 }}>
                {categoryMetrics.map((metric, idx) => (
                  <Box
                    key={idx}
                    onClick={() => handleCopyField(metric.userFriendly)}
                    sx={{
                      display: 'flex', alignItems: 'center', gap: 2, p: 1.5, mb: 0.5,
                      borderRadius: 1, cursor: 'pointer',
                      background: 'rgba(0,0,0,0.15)',
                      '&:hover': { background: 'rgba(0,0,0,0.25)' },
                    }}
                  >
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.3 }}>
                        <code style={{
                          backgroundColor: 'rgba(0,212,255,0.1)',
                          color: '#00d4ff',
                          padding: '2px 8px',
                          borderRadius: 4,
                          fontSize: '0.8rem',
                          fontFamily: 'monospace',
                          fontWeight: 600,
                        }}>
                          {metric.userFriendly}
                        </code>
                        <Chip label={metric.unit} size="small" variant="outlined"
                          sx={{ height: 20, fontSize: '0.7rem', color: 'rgba(255,255,255,0.5)', borderColor: 'rgba(255,255,255,0.15)' }} />
                      </Box>
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.4)' }}>
                        {metric.description} &middot; <span style={{ fontFamily: 'monospace' }}>{metric.internal}</span>
                      </Typography>
                    </Box>
                    <Typography variant="body2" sx={{ fontFamily: 'monospace', color: '#4caf50', fontWeight: 600, whiteSpace: 'nowrap' }}>
                      {metric.example}
                    </Typography>
                    <Tooltip title={copiedField === metric.userFriendly ? 'Copied!' : 'Copy'}>
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleCopyField(metric.userFriendly); }}>
                        {copiedField === metric.userFriendly ? (
                          <CheckCircle sx={{ color: '#4caf50', fontSize: 18 }} />
                        ) : (
                          <ContentCopy sx={{ fontSize: 16, color: 'rgba(255,255,255,0.3)' }} />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Box>
                ))}
              </Box>
            </Collapse>
          </Box>
        );
      })}

      {filteredMetrics.length === 0 && (
        <Typography sx={{ color: 'rgba(255,255,255,0.3)', textAlign: 'center', py: 3 }}>
          No metrics found matching "{searchTerm}"
        </Typography>
      )}

      <Box sx={{ mt: 2, p: 1.5, background: 'rgba(76,175,80,0.08)', borderRadius: 1, border: '1px solid rgba(76,175,80,0.15)' }}>
        <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.5)' }}>
          <strong>Tip:</strong> You don't need to send all fields. Only include the measurements your sensor provides. Missing values are handled automatically.
        </Typography>
      </Box>
    </Box>
  );
};

export default SensorMetricsReference;
